import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox, Spin } from "antd";
import "./styles.scss";
import "./sudanLanding.scss";
import Navbar from "./Navbar";
import Banner from "../assets/Banner.svg";
import { FormattedMessage } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { login } from "../../authSlice";
import { EyeInvisible, EyeVisible } from "../../../../assets/icons";
import AuthService from "../../../../base/services/authentication.service";
import { UserRolesEnum } from "../../userRoles.enum";
import { useLanguage } from "../i18n/LanguageContext";

const Header = () => {
   const { t } = useLanguage();
   const initialValues = {
      email: "",
      password: ""
   };

   const [checked, setChecked] = useState(false);
   const [icon, setIcon] = useState(EyeInvisible);
   const [type, setType] = useState("password");
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const { isLoading } = useSelector((state) => state.auth);

   const onChange = (e) => {
      setChecked(e.target.checked);
   };

   const LoginSchema = Yup.object().shape({
      email: Yup.string().email("Invalid email").required("Required"),
      password: Yup.string().max(16, "Maximum 16 symbols").required("Required")
   });

   const handleToggle = () => {
      if (type === "password") {
         setIcon(EyeVisible);
         setType("text");
      } else {
         setIcon(EyeInvisible);
         setType("password");
      }
   };

   const submitForm = (values) => {
      dispatch(login(values, successCallback, errCB));
   };

   const successCallback = () => {
      if (AuthService.isAuthorized([UserRolesEnum.SuperAdmin])) {
         navigate("/admin/workspace");
      } else if (AuthService.isAuthorized([UserRolesEnum.Approver])) {
         navigate("/approver/submissions");
      } else {
         navigate("/workspace");
      }
   };

   const errCB = (data) => {
      if (data.target?.targetUrl) {
         navigate(data.target?.targetUrl, {
            state: { fromApp: true, data: data.target.params }
         });
      }
   };

   return (
      <header>
         <Navbar />

         {/* ---------- Hero ---------- */}
         <section className="sd-hero" id="top">
            <div className="sd-hero-bg">
               <img src={Banner} alt="" aria-hidden="true" />
            </div>
            <div className="container">
               <div className="sd-hero-inner">
                  <div className="sd-hero-text">
                     <span className="sd-chip">{t("Aligned with the IASC Humanitarian Reset")}</span>
                     <h1>
                        {t("Sudanese organizations are leading the response.")}{" "}
                        <span className="sd-accent">
                           {t("KAPorg empowers you to lead the funding too.")}
                        </span>
                     </h1>
                     <p>
                        {t(
                           "A free digital platform built to help Sudanese NGOs and CSOs assess their capacity, demonstrate organizational readiness, and unlock direct access to international funding."
                        )}
                     </p>
                     <div className="sd-hero-cta">
                        <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                           {t("Register for free")}
                        </Link>
                        <Link
                           to="/capacity-accelerator-program"
                           className="sd-btn sd-btn-ghost-light"
                        >
                           {t("Capacity Accelerator Program")}
                        </Link>
                     </div>
                  </div>

                  {/* ---------- Login card ---------- */}
                  <div className="sd-hero-form">
                     <div className="sd-login-card">
                        <div className="sd-login-head">
                           <h3>{t("Welcome back!")}</h3>
                           <p>{t("Login into your account")}</p>
                        </div>
                        <Formik
                           initialValues={initialValues}
                           validationSchema={LoginSchema}
                           onSubmit={(values) => submitForm(values)}
                        >
                           {({ handleSubmit, handleChange, values, errors }) => (
                              <form onSubmit={handleSubmit} className="form">
                                 <div className="sd-field">
                                    <label>{t("Email")}</label>
                                    <input
                                       placeholder="yourmail@gmail.com"
                                       className="form-control"
                                       type="email"
                                       name="email"
                                       onChange={handleChange}
                                       value={values.email}
                                    />
                                    {errors.email && (
                                       <div className="sd-error">{errors.email}</div>
                                    )}
                                 </div>
                                 <div className="sd-field">
                                    <label>{t("Password")}</label>
                                    <div className="sd-password">
                                       <input
                                          autoComplete="off"
                                          placeholder={t("Password")}
                                          className="form-control"
                                          type={type}
                                          name="password"
                                          onChange={handleChange}
                                          value={values.password}
                                       />
                                       <img
                                          className="sd-eye"
                                          onClick={handleToggle}
                                          src={icon}
                                          alt="show password"
                                       />
                                    </div>
                                    {errors.password && (
                                       <div className="sd-error">{errors.password}</div>
                                    )}
                                 </div>

                                 <div className="sd-login-row">
                                    <div className="sd-remember">
                                       <Checkbox
                                          checked={checked}
                                          onChange={onChange}
                                          style={{ lineHeight: "0px" }}
                                       />
                                       <span>{t("Remember me?")}</span>
                                    </div>
                                    <Link to="/auth/forgot-password" id="kt_login_forgot">
                                       <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                                    </Link>
                                 </div>

                                 <button
                                    id="kt_login_signin_submit"
                                    type="submit"
                                    className="sd-btn sd-btn-primary sd-submit"
                                 >
                                    {isLoading ? <Spin /> : t("Log in")}
                                 </button>

                                 <p className="sd-signup">
                                    {t("Don't have an account yet?")}
                                    <Link to="/auth/registration" id="kt_login_signup">
                                       {t("Sign up")}
                                    </Link>
                                 </p>
                              </form>
                           )}
                        </Formik>
                     </div>
                  </div>
               </div>
            </div>
         </section>
      </header>
   );
};

export default Header;
